import { httpsCallable } from 'firebase/functions';
import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { db, functions } from '../firebase';

async function callBackend(functionName, payload = {}) {
  const callable = httpsCallable(functions, functionName);
  const response = await callable(payload);
  return response.data;
}

const sortClients = (clients = []) => ([...clients].sort((a, b) => String(a.businessName || a.name || '').localeCompare(String(b.businessName || b.name || ''), 'es')));

async function listAdminClientsFallback() {
  const snapshot = await getDocs(collection(db, 'clients'));
  return sortClients(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) })));
}

async function listAdminUnreadConversationsFallback() {
  const clientsSnapshot = await getDocs(collection(db, 'clients'));
  const clients = clientsSnapshot.docs.map((docSnap) => ({ id: docSnap.id, ...(docSnap.data() || {}) }));

  const results = await Promise.all(clients.map(async (client) => {
    const conversationId = String(client.email || '').trim().toLowerCase();
    if (!conversationId) return null;

    const unreadQuery = query(
      collection(db, 'conversations', conversationId, 'messages'),
      where('from', '==', 'client'),
      where('read', '==', false),
      limit(1)
    );

    const unreadSnapshot = await getDocs(unreadQuery);
    if (unreadSnapshot.empty) return null;

    const latestUnread = unreadSnapshot.docs[0];
    return {
      clientId: client.id,
      clientEmail: conversationId,
      clientName: client.businessName || client.name || conversationId,
      latestMessage: {
        id: latestUnread.id,
        ...(latestUnread.data() || {}),
      },
    };
  }));

  return results.filter(Boolean);
}

export const listAdminClients = async () => {
  try {
    const data = await callBackend('listAdminClients');
    return data.clients || [];
  } catch (error) {
    console.warn('listAdminClients callable fallo; usando fallback Firestore.', error);
    return listAdminClientsFallback();
  }
};

export const saveAdminClient = async (payload) => {
  const data = await callBackend('saveAdminClient', payload);
  return data.client;
};

export const deleteAdminClient = async (clientId) => {
  return callBackend('deleteAdminClient', { clientId });
};

export const getAdminClientInvoices = async (clientId) => {
  return callBackend('getAdminClientInvoices', { clientId });
};

export const saveClientInvoice = async (payload) => {
  const data = await callBackend('saveClientInvoice', payload);
  return data.invoice;
};

export const requestClientInvoicePayment = async (payload) => {
  const data = await callBackend('requestClientInvoicePayment', payload);
  return data.invoice;
};

export const listAdminUnreadConversations = async () => {
  try {
    const data = await callBackend('listAdminUnreadConversations');
    return data.conversations || [];
  } catch (error) {
    console.warn('listAdminUnreadConversations callable fallo; usando fallback Firestore.', error);
    return listAdminUnreadConversationsFallback();
  }
};

export const getAdminConversation = async (clientId) => {
  return callBackend('getAdminConversation', { clientId });
};

export const sendAdminConversationMessage = async (payload) => {
  const data = await callBackend('sendAdminConversationMessage', payload);
  return data.message;
};

export const getClientConversation = async () => {
  return callBackend('getClientConversation');
};

export const sendClientConversationMessage = async (payload) => {
  const data = await callBackend('sendClientConversationMessage', payload);
  return data.message;
};

export const markConversationMessagesRead = async (payload = {}) => {
  return callBackend('markConversationMessagesRead', payload);
};

export const listClientServiceTickets = async () => {
  return callBackend('listClientServiceTickets');
};

export const createClientServiceTicket = async (payload) => {
  const data = await callBackend('createClientServiceTicket', payload);
  return data.ticket;
};

export const listClientServiceTicketsByClient = async (clientId) => {
  return callBackend('listClientServiceTicketsByClient', { clientId });
};

export const getServiceTicketDetails = async (payload) => {
  return callBackend('getServiceTicketDetails', payload);
};

export const addServiceTicketTimelineEntry = async (payload) => {
  return callBackend('addServiceTicketTimelineEntry', payload);
};

export const updateServiceTicketStatus = async (payload) => {
  const data = await callBackend('updateServiceTicketStatus', payload);
  return data.ticket;
};

export const listServiceDeskTickets = async () => {
  const data = await callBackend('listServiceDeskTickets');
  return data.tickets || [];
};
